import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Nature & Environment — 8 templates
   mountain_landscape, volcano_cross_section, ocean_layers,
   ecosystem_web, river_delta, rock_strata, weather_system,
   forest_canopy
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Mountain Landscape ───────────────────────────────── */
export const MountainLandscape: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Mountains";
  const accentColor = (props.accentColor as string) || "#4caf50";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="ml-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#ml-sky)" rx="16" />
      {/* Sun */}
      <circle cx="650" cy="100" r="45" fill="#fff9c4" opacity="0.8" />
      {/* Back mountains */}
      <polygon points="0,400 150,180 300,350 450,200 600,300 800,250 800,400" fill="#37474f" opacity="0.6" />
      {/* Front mountains */}
      <polygon points="0,420 200,120 380,350 500,160 700,320 800,280 800,500 0,500" fill="#455a64" />
      {/* Snow caps */}
      <polygon points="200,120 170,180 230,180" fill="#eceff1" opacity="0.9" />
      <polygon points="500,160 470,210 530,210" fill="#eceff1" opacity="0.9" />
      {/* Trees */}
      {[100, 300, 550, 700].map((x, i) => (
        <g key={i}>
          <polygon points={`${x},420 ${x - 15},460 ${x + 15},460`} fill={accentColor} opacity="0.8" />
          <polygon points={`${x},400 ${x - 12},435 ${x + 12},435`} fill={accentColor} opacity="0.9" />
          <rect x={x - 3} y="460" width="6" height="15" fill="#5d4037" />
        </g>
      ))}
      {/* Ground */}
      <rect x="0" y="475" width="800" height="125" fill="#2e7d32" rx="0" />
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Volcano Cross Section ────────────────────────────── */
export const VolcanoCrossSection: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Volcano";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#1a1a2e" rx="16" />
      {/* Volcano exterior */}
      <polygon points="200,500 350,180 400,160 450,180 600,500" fill="#5d4037" stroke="#4e342e" strokeWidth="2" />
      {/* Magma chamber */}
      <ellipse cx="400" cy="480" rx="120" ry="50"
        fill={activeIndex === 0 ? "#ff5722" : "#e64a19"} opacity={activeIndex === 0 ? 1 : 0.7} />
      {/* Conduit */}
      <rect x="385" y="250" width="30" height="230" fill="#ff5722" opacity="0.6" />
      {/* Lava layers */}
      {[
        { y: 380, color: "#e64a19" }, { y: 340, color: "#ff5722" }, { y: 300, color: "#ff7043" },
      ].map((l, i) => (
        <rect key={i} x="388" y={l.y} width="24" height="30" fill={l.color} opacity="0.5" />
      ))}
      {/* Eruption / smoke */}
      {flowProgress > 0.3 && (
        <g opacity={flowProgress}>
          <ellipse cx="400" cy="130" rx={30 + flowProgress * 40} ry={20 + flowProgress * 20} fill="#757575" opacity="0.4" />
          <ellipse cx="390" cy="100" rx={20 + flowProgress * 30} ry={15 + flowProgress * 15} fill="#9e9e9e" opacity="0.3" />
          {/* Lava spray */}
          {[380, 400, 420].map((x, i) => (
            <circle key={i} cx={x} cy={160 - flowProgress * 30 - i * 15} r={4} fill="#ff5722" opacity={0.7 - i * 0.15} />
          ))}
        </g>
      )}
      {/* Labels */}
      {[
        { x: 250, y: 490, t: "Magma Chamber" },
        { x: 300, y: 350, t: "Conduit" },
        { x: 400, y: 140, t: "Crater" },
      ].map((l, i) => (
        <text key={i} x={l.x} y={l.y} fill="#ecf0f1" fontSize="12" opacity="0.7">{l.t}</text>
      ))}
      <text x="400" y="565" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Ocean Layers ─────────────────────────────────────── */
export const OceanLayers: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Ocean Zones";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const zones = (props.zones as string[]) || ["Sunlight Zone", "Twilight Zone", "Midnight Zone", "Abyssal Zone", "Hadal Zone"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {[
        { y: 40, h: 100, c: "#2196f3" }, { y: 140, h: 100, c: "#1565c0" },
        { y: 240, h: 110, c: "#0d47a1" }, { y: 350, h: 110, c: "#1a237e" },
        { y: 460, h: 100, c: "#0d1b2a" },
      ].map((z, i) => (
        <g key={i}>
          <rect x="100" y={z.y} width="600" height={z.h}
            fill={z.c} opacity={i === activeIndex ? 1 : 0.6}
            stroke={i === activeIndex ? "#fff" : "none"} strokeWidth="2" />
          <text x="130" y={z.y + z.h / 2 + 5} fill="#ecf0f1" fontSize="14" fontWeight="500">
            {zones[i]}
          </text>
          {/* Depth */}
          <text x="680" y={z.y + z.h / 2 + 5} textAnchor="end" fill="#bdc3c7" fontSize="11">
            {["0-200m", "200-1000m", "1000-4000m", "4000-6000m", "6000m+"][i]}
          </text>
        </g>
      ))}
      {/* Marine life icons */}
      <text x="500" y="85" fontSize="18">🐠</text>
      <text x="450" y="190" fontSize="16">🦈</text>
      <text x="380" y="300" fontSize="14">🐙</text>
      <text x="350" y="420" fontSize="12">🦑</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Ecosystem Web ────────────────────────────────────── */
export const EcosystemWeb: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Ecosystem";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const organisms = (props.organisms as string[]) || ["Sun", "Plants", "Herbivores", "Carnivores", "Decomposers"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Nodes arranged in trophic levels */}
      {[
        { x: 400, y: 80, c: "#f9a825", r: 35 },   // Sun
        { x: 250, y: 200, c: "#4caf50", r: 30 },   // Plants
        { x: 550, y: 200, c: "#4caf50", r: 30 },   // Plants
        { x: 300, y: 340, c: "#ff9800", r: 28 },   // Herbivores
        { x: 500, y: 340, c: "#ff9800", r: 28 },   // Herbivores
        { x: 400, y: 440, c: "#e53935", r: 26 },   // Carnivores
      ].map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r}
            fill={i === activeIndex ? "#fff" : n.c}
            opacity={i === activeIndex ? 0.9 : 0.6}
            stroke={i === activeIndex ? "#fff" : "#546e7a"} strokeWidth="2" />
        </g>
      ))}
      {/* Arrows (energy flow) */}
      {[
        [400, 80, 250, 200], [400, 80, 550, 200],
        [250, 200, 300, 340], [550, 200, 500, 340],
        [300, 340, 400, 440], [500, 340, 400, 440],
      ].map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1 + 30} x2={x2} y2={y2 - 30}
          stroke="#546e7a" strokeWidth="1.5" markerEnd="url(#ew-arrow)" />
      ))}
      <defs>
        <marker id="ew-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#546e7a" />
        </marker>
      </defs>
      {/* Labels */}
      {[
        { x: 400, y: 80, t: organisms[0] }, { x: 250, y: 200, t: organisms[1] },
        { x: 400, y: 340, t: organisms[2] }, { x: 400, y: 440, t: organisms[3] },
      ].map((l, i) => (
        <text key={i} x={l.x} y={l.y + 50} textAnchor="middle" fill="#bdc3c7" fontSize="12">{l.t}</text>
      ))}
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. River Delta ──────────────────────────────────────── */
export const RiverDelta: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "River Delta";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#1b5e20" rx="16" />
      {/* Main river */}
      <path d="M400,40 Q390,120 400,200" fill="none" stroke="#1565c0" strokeWidth="20" strokeLinecap="round"
        strokeDasharray="300" strokeDashoffset={300 - flowProgress * 300} />
      {/* Branches */}
      {[
        "M400,200 Q300,300 200,420", "M400,200 Q350,320 280,480",
        "M400,200 Q400,320 400,500", "M400,200 Q450,320 520,480",
        "M400,200 Q500,300 600,420",
      ].map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#1976d2" strokeWidth={12 - i * 1.5}
          strokeLinecap="round" opacity="0.7"
          strokeDasharray="400" strokeDashoffset={400 - Math.max(0, flowProgress * 2 - 0.5) * 400} />
      ))}
      {/* Ocean at bottom */}
      <rect x="0" y="470" width="800" height="130" fill="#0d47a1" opacity="0.5" rx="0" />
      {/* Sediment deposits */}
      {[200, 300, 400, 500, 600].map((x, i) => (
        <ellipse key={i} cx={x} cy={460 - i * 5} rx={30 + i * 5} ry={8} fill="#8d6e63" opacity="0.4" />
      ))}
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Rock Strata ──────────────────────────────────────── */
export const RockStrata: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Rock Layers";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Topsoil", "Sandstone", "Limestone", "Shale", "Granite", "Basalt"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {[
        { color: "#6d4c41", pattern: "dots" }, { color: "#f9a825", pattern: "lines" },
        { color: "#bdbdbd", pattern: "cross" }, { color: "#546e7a", pattern: "dash" },
        { color: "#e91e63", pattern: "solid" }, { color: "#37474f", pattern: "solid" },
      ].map((l, i) => {
        const y = 60 + i * 80;
        return (
          <g key={i}>
            <rect x="120" y={y} width="480" height="75"
              fill={i === activeIndex ? "#fff" : l.color}
              opacity={i === activeIndex ? 0.8 : 0.5}
              stroke={i === activeIndex ? "#fff" : "#00000030"} strokeWidth={i === activeIndex ? 2 : 1} />
            {/* Pattern overlay */}
            {l.pattern === "dots" && Array.from({ length: 8 }, (_, j) => (
              <circle key={j} cx={160 + j * 55} cy={y + 37} r="3" fill="#fff" opacity="0.15" />
            ))}
            {l.pattern === "lines" && Array.from({ length: 6 }, (_, j) => (
              <line key={j} x1={140 + j * 80} y1={y + 10} x2={140 + j * 80} y2={y + 65} stroke="#fff" strokeWidth="0.8" opacity="0.1" />
            ))}
            {/* Label */}
            <text x="620" y={y + 42} fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="14" fontWeight="500">
              {layers[i]}
            </text>
            {/* Depth marker */}
            <text x="105" y={y + 42} textAnchor="end" fill="#7f8c8d" fontSize="10">
              {`${i * 50}m`}
            </text>
          </g>
        );
      })}
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Weather System ───────────────────────────────────── */
export const WeatherSystem: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Weather";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="ws-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1565c0" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#ws-sky)" rx="16" />
      {/* Sun */}
      <circle cx="650" cy="100" r="50" fill="#fff9c4" opacity={activeIndex === 0 ? 1 : 0.5} />
      {activeIndex === 0 && [0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1={650 + Math.cos(a * Math.PI / 180) * 60} y1={100 + Math.sin(a * Math.PI / 180) * 60}
          x2={650 + Math.cos(a * Math.PI / 180) * 75} y2={100 + Math.sin(a * Math.PI / 180) * 75}
          stroke="#fff9c4" strokeWidth="3" strokeLinecap="round" />
      ))}
      {/* Cloud */}
      <g opacity={activeIndex === 1 ? 1 : 0.6} transform={`translate(${Math.sin(phase * 0.5) * 10}, 0)`}>
        <ellipse cx="300" cy="150" rx="80" ry="40" fill="#eceff1" />
        <ellipse cx="350" cy="135" rx="60" ry="35" fill="#e0e0e0" />
        <ellipse cx="260" cy="140" rx="55" ry="30" fill="#e0e0e0" />
      </g>
      {/* Rain */}
      {activeIndex >= 2 && (
        <g>
          {Array.from({ length: 12 }, (_, i) => (
            <line key={i} x1={240 + i * 20 + Math.sin(phase + i) * 5} y1={200 + (i % 4) * 15}
              x2={235 + i * 20 + Math.sin(phase + i) * 5} y2={220 + (i % 4) * 15}
              stroke="#90caf9" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          ))}
        </g>
      )}
      {/* Lightning */}
      {activeIndex === 3 && (
        <polygon points="350,190 330,260 345,260 320,340 370,250 355,250 380,190" fill="#fff9c4" opacity="0.8" />
      )}
      {/* Ground */}
      <rect x="0" y="480" width="800" height="120" fill="#2e7d32" />
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Forest Canopy ────────────────────────────────────── */
export const ForestCanopy: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Forest Layers";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Emergent", "Canopy", "Understory", "Forest Floor"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Sky */}
      <rect x="0" y="0" width="800" height="100" fill="#1565c0" opacity="0.3" rx="16" />
      {/* Four layers */}
      {[
        { y: 40, h: 100, c: "#1b5e20", trees: [[200, 60], [500, 50]] },
        { y: 140, h: 140, c: "#2e7d32", trees: [[150, 70], [350, 80], [600, 65]] },
        { y: 280, h: 120, c: "#388e3c", trees: [[120, 40], [300, 45], [480, 50], [650, 35]] },
        { y: 400, h: 160, c: "#4e342e", trees: [] },
      ].map((l, i) => (
        <g key={i}>
          <rect x="60" y={l.y} width="680" height={l.h}
            fill={l.c} opacity={i === activeIndex ? 0.9 : 0.35}
            stroke={i === activeIndex ? "#fff" : "none"} strokeWidth="2" />
          {/* Trees/bushes */}
          {l.trees.map(([tx, th], j) => (
            <g key={j}>
              <ellipse cx={tx} cy={l.y + l.h / 2} rx={th / 2} ry={th / 2.5}
                fill={l.c} opacity="0.7" stroke="#1b5e20" strokeWidth="1" />
            </g>
          ))}
          {/* Layer label */}
          <text x="720" y={l.y + l.h / 2 + 5} fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="13" fontWeight="500"
            textAnchor="end">{layers[i]}</text>
        </g>
      ))}
      {/* Mushrooms on forest floor */}
      {[200, 400, 550].map((x, i) => (
        <g key={i}>
          <rect x={x - 2} y="520" width="4" height="12" fill="#bdbdbd" />
          <ellipse cx={x} cy="520" rx="10" ry="6" fill="#e53935" opacity="0.6" />
        </g>
      ))}
      <text x="400" y="585" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
